// src/components/AlbumMediaModal.jsx
import React, { useState, useEffect, useRef } from 'react';
import { 
  X, 
  ChevronLeft, 
  ChevronRight, 
  Download, 
  Share2, 
  Heart,
  ZoomIn,
  ZoomOut,
  RotateCw,
  Play,
  Mic,
  MoreVertical,
  MessageCircle,
  Plus,
  Pause,
  Volume2,
  VolumeX,
} from 'lucide-react';

export const MediaLightbox = ({ 
  bookData, 
  media, 
  currentIndex, 
  onClose, 
  onNavigate,
  onOpenExpressions,   
  onOpenVoiceRecorder 
}) => {
  const [scale, setScale] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [isTouchDevice, setIsTouchDevice] = useState(false);
  const [isMobileDevice, setIsMobileDevice] = useState(false);
  const [controlsVisible, setControlsVisible] = useState(false);
  const [showActionMenu, setShowActionMenu] = useState(false);
  const [hasLiked, setHasLiked] = useState(false);

  const videoRef = useRef(null);
  const mediaContainerRef = useRef(null);
  const touchTimeoutRef = useRef(null);
  
  // Touch/swipe handling
  const [touchStart, setTouchStart] = useState({ x: 0, y: 0 });
  const [touchEnd, setTouchEnd] = useState({ x: 0, y: 0 });

  const currentMedia = media[currentIndex];
  const isVideo = currentMedia?.type === 'video';

  const [showActions, setShowActions] = useState(false);

  // Detect touch device
  useEffect(() => {
    const checkTouchDevice = () => {
      const isTouchEnabled = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
      setIsTouchDevice(isTouchEnabled);
      setControlsVisible(!isTouchEnabled); // Always show on desktop, hidden on mobile initially
    };
    
    checkTouchDevice();
    window.addEventListener('resize', checkTouchDevice);
    return () => window.removeEventListener('resize', checkTouchDevice);
  }, []);

  const latestOnCloseRef = useRef(onClose);
    useEffect(() => {
      latestOnCloseRef.current = onClose;
    }, [onClose]);

    useEffect(() => {
      // Guard against double registration (React 18 Strict Mode)
      let armed = true;

      // Push a history entry so the *first* Back stays on the same page
      // (i.e., it triggers popstate instead of leaving your gallery view).
      window.history.pushState({ __lightbox: true }, '', window.location.href);

      const handleBack = () => {
        if (!armed) return;
        // Immediately re-insert a state so that after we close the modal,
        // a subsequent Back press behaves normally for the underlying page.
        window.history.pushState({ __lightbox: true }, '', window.location.href);
        // Now close the modal
        latestOnCloseRef.current?.();
        // Disarm so we don't fire twice
        armed = false;
      };

      const onPopState = () => handleBack();
      const onHashChange = () => handleBack(); // Safari/iOS quirk safety net

      window.addEventListener('popstate', onPopState);
      window.addEventListener('hashchange', onHashChange);

      return () => {
        window.removeEventListener('popstate', onPopState);
        window.removeEventListener('hashchange', onHashChange);
        armed = false;
      };
    }, []);



  useEffect(() => {
    const handleKeyDown = (e) => {
      switch (e.key) {
        case 'Escape':
          onClose();
          break;
        case 'ArrowLeft':
          handlePrevious();
          break;
        case 'ArrowRight':
          handleNext();
          break;
        case ' ':
          e.preventDefault();
          if (isVideo) togglePlayPause();
          break;
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'auto';
    };
  }, [currentIndex, isVideo]);

  // Reset transformations when media changes
  useEffect(() => {
    setScale(1);
    setRotation(0);
    setIsPlaying(false);
  }, [currentIndex]);

  // Auto-hide controls on touch devices after 3 seconds
  useEffect(() => {
    if (isTouchDevice && controlsVisible) {
      if (touchTimeoutRef.current) {
        clearTimeout(touchTimeoutRef.current);
      }
      
      touchTimeoutRef.current = setTimeout(() => {
        setControlsVisible(false);
      }, 5000);
    }

    return () => {
      if (touchTimeoutRef.current) {
        clearTimeout(touchTimeoutRef.current);
      }
    };
  }, [controlsVisible, isTouchDevice]);

  // Touch handlers for swipe navigation
  const handleTouchStart = (e) => {
    if (!isTouchDevice) return;
    const touch = e.touches[0];
    setTouchStart({ x: touch.clientX, y: touch.clientY });
  };

  const handleTouchMove = (e) => {
    if (!isTouchDevice) return;
    const touch = e.touches[0];
    setTouchEnd({ x: touch.clientX, y: touch.clientY });
  };

  const handleTouchEnd = () => {
    if (!isTouchDevice || !touchStart.x || !touchEnd.x) return;
    
    const deltaX = touchStart.x - touchEnd.x;
    const deltaY = Math.abs(touchStart.y - touchEnd.y);
    const minSwipeDistance = 50;
    
    // Only process horizontal swipes (ignore vertical scrolling)
    if (Math.abs(deltaX) > minSwipeDistance && deltaY < 100) {
      if (deltaX > 0) {
        // Swipe left - next image
        handleNext();
      } else {
        // Swipe right - previous image
        handlePrevious();
      }
    }
    
    // Reset touch positions
    setTouchStart({ x: 0, y: 0 });
    setTouchEnd({ x: 0, y: 0 });
  };

  const handleMediaTouch = () => {
    if (isTouchDevice) {
      setControlsVisible(prev => !prev);
    }
  };

  const handlePrevious = () => {
    const newIndex = currentIndex > 0 ? currentIndex - 1 : media.length - 1;
    onNavigate(newIndex);
  };

  const handleNext = () => {
    const newIndex = currentIndex < media.length - 1 ? currentIndex + 1 : 0;
    onNavigate(newIndex);
  };

  const handleZoomIn = () => setScale(prev => Math.min(prev * 1.2, 3));
  const handleZoomOut = () => setScale(prev => Math.max(prev / 1.2, 0.5));
  const handleRotate = () => setRotation(prev => (prev + 90) % 360);

  const togglePlayPause = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const toggleMute = () => {
    if (videoRef.current) {
      videoRef.current.muted = !isMuted;
      setIsMuted(!isMuted);
    }
  };

  const handleShare = async () => {
    const shareData = {
      title: currentMedia.title || 'Check out this media',
      text: currentMedia.description || '',
      url: window.location.href
    };

    if (navigator.share) {
      try {
        await navigator.share(shareData);
      } catch (error) {
        console.log('Share cancelled');
      }
    } else {
      navigator.clipboard.writeText(shareData.url);
      alert('Link copied to clipboard!');
    }
  };

  const handleDownload = async () => {
    try {
      const response = await fetch(currentMedia.url);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      
      const a = document.createElement('a');
      a.href = url;
      a.download = currentMedia.title || 'media-file';
      document.body.appendChild(a);
      a.click();
      
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Download failed:', error);
    }
  };

  const handleToggleActionMenu = (e) => {
    e.stopPropagation();
    setShowActionMenu(!showActionMenu);
  };

  const handleLike = async (e) => {
    e.stopPropagation();
    setHasLiked(!hasLiked);
    // You can add analytics or save to database here
    console.log('Media liked:', currentMedia.id);
  };

  const handleOpenExpressions = (e) => {
    e.stopPropagation();
    setShowActionMenu(false);
    onOpenExpressions?.(currentMedia, currentMedia.galleryId || bookData?.activeGalleryId);
  };

  const handleOpenVoiceRecorder = (e) => {
    e.stopPropagation();
    setShowActionMenu(false);
    onOpenVoiceRecorder?.({
      level: 'media',
      data: { 
        mediaItem: currentMedia, 
        galleryId: currentMedia.galleryId || bookData?.activeGalleryId
      }
    });
  };

  const interactions = [];
  const bookSettings = bookData;

  if (!currentMedia) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-95 z-50">
      {/* Header - Always visible on desktop, conditional on mobile */}
      <div className={`absolute top-0 left-0 right-0 bg-gradient-to-b from-black/70 to-transparent p-4 flex items-center justify-between z-20 transition-opacity duration-300 ${
        isTouchDevice ? (controlsVisible ? 'opacity-100' : 'opacity-0 pointer-events-none') : 'opacity-100'
      }`}>
        <div className="flex items-center gap-4">
          <span className="text-white text-sm font-medium">
            {currentIndex + 1} of {media.length}
          </span>
          {currentMedia.title && (
            <h3 className="text-white font-medium truncate max-w-xs">{currentMedia.title}</h3>
          )}
        </div>

        <div className="flex items-center gap-2">
          {!isVideo && (
            <>
              <button
                onClick={handleZoomOut}
                className="text-white hover:bg-white hover:bg-opacity-20 p-2 rounded-lg transition-colors"
                title="Zoom Out"
              >
                <ZoomOut className="h-5 w-5" />
              </button>
              <button
                onClick={handleZoomIn}
                className="text-white hover:bg-white hover:bg-opacity-20 p-2 rounded-lg transition-colors"
                title="Zoom In"
              >
                <ZoomIn className="h-5 w-5" />
              </button>
              <button
                onClick={handleRotate}
                className="text-white hover:bg-white hover:bg-opacity-20 p-2 rounded-lg transition-colors"
                title="Rotate"
              >
                <RotateCw className="h-5 w-5" />
              </button>
            </>
          )}

          {isVideo && (
            <>
              <button
                onClick={togglePlayPause}
                className="text-white hover:bg-white hover:bg-opacity-20 p-2 rounded-lg transition-colors"
                title={isPlaying ? "Pause" : "Play"}
              >
                {isPlaying ? <Pause className="h-5 w-5" /> : <Play className="h-5 w-5" />}
              </button>
              <button
                onClick={toggleMute}
                className="text-white hover:bg-white hover:bg-opacity-20 p-2 rounded-lg transition-colors"
                title={isMuted ? "Unmute" : "Mute"}
              >
                {isMuted ? <VolumeX className="h-5 w-5" /> : <Volume2 className="h-5 w-5" />}
              </button>
            </>
          )}

          <button
            onClick={handleShare}
            className="text-white hover:bg-white hover:bg-opacity-20 p-2 rounded-lg transition-colors"
            title="Share"
          >
            <Share2 className="h-5 w-5" />
          </button>

          {bookSettings?.allowDownloads && (
            <button
              onClick={handleDownload}
              className="bg-gray-600 text-white rounded-full p-2"
              title="Download"
            >
              <Download className="h-4 w-4" />
            </button>
          )}

          <button
            onClick={onClose}
            className="text-white hover:bg-white hover:bg-opacity-20 p-2 rounded-lg transition-colors"
            title="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
      </div>

      {/* Navigation Buttons */}
      {media.length > 1 && (
        <>
          <button
            onClick={handlePrevious}
            className={`absolute z-20 top-1/2 bg-black bg-opacity-50 hover:bg-opacity-70 text-white p-3 rounded-full transition-all duration-300 ${
              isTouchDevice 
                ? `left-4 top-20 ${controlsVisible ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-2 pointer-events-none'}` 
                : 'left-4 top-1/2 -translate-y-1/2 opacity-80 hover:opacity-100'
            }`}
            title="Previous"
          >
            <ChevronLeft className="h-6 w-6" />
          </button>
          <button
            onClick={handleNext}
            className={`absolute z-20 bg-black bg-opacity-50 hover:bg-opacity-70 text-white p-3 rounded-full transition-all duration-300 ${
              isTouchDevice 
                ? `right-4 top-20 ${controlsVisible ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-2 pointer-events-none'}` 
                : 'right-4 top-1/2 -translate-y-1/2 opacity-80 hover:opacity-100'
            }`}
            title="Next"
          >
            <ChevronRight className="h-6 w-6" />
          </button>
        </>
      )}

      {/* Media Container */}
        <div 
          ref={mediaContainerRef}
          className="absolute inset-0 flex items-center justify-center"
          style={{
            paddingTop: isTouchDevice ? '0' : '80px',
            paddingBottom: isTouchDevice ? '0' : '80px',
            paddingLeft: isTouchDevice ? '0' : '16px',
            paddingRight: isTouchDevice ? '0' : '16px'
          }}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          onClick={handleMediaTouch}
        >
          {currentMedia.type === 'youtube' ? (
          // YouTube in Lightbox
          <div className="relative w-full bg-black" style={{ paddingBottom: '56.25%' }}>
            <iframe
              src={`https://www.youtube.com/embed/${currentMedia.videoId}?autoplay=1&rel=0`}
              title={currentMedia.title || 'YouTube video'}
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              className="absolute top-0 left-0 w-full h-full"
            />
          </div>
        ) : currentMedia.type === 'video' ? (
          // Regular video in lightbox
          <video
            ref={videoRef}
            src={currentMedia.url}
            controls
            autoPlay
            muted={isMuted}
            loop
            className="w-full max-h-[80vh] object-contain"
            onPlay={() => setIsPlaying(true)}
            onPause={() => setIsPlaying(false)}
          >
            <source src={currentMedia.url} type={currentMedia.mimeType || 'video/mp4'} />
          </video>
        ) : (
          <img
            src={currentMedia.url}
            alt={currentMedia.title || 'Media'}
            className={`transition-all duration-300 object-contain ${
              isTouchDevice 
                ? 'w-full h-auto max-h-full' // Mobile: fit width
                : 'h-full w-auto max-w-full' // Desktop: fit height
            }`}
            style={{
              transform: `scale(${scale}) rotate(${rotation}deg)`,
              cursor: isTouchDevice ? 'pointer' : (scale === 1 ? 'zoom-in' : 'grab'),
            }}
            onClick={!isTouchDevice && scale === 1 ? handleZoomIn : undefined}
            draggable={false}
          />
        )}
      </div>

      {/* Media Info - Conditional on mobile */}
      {(currentMedia.description || currentMedia.tags?.length > 0) && (
        <div className={`absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-4 transition-opacity duration-300 ${
          isTouchDevice ? (controlsVisible ? 'opacity-100' : 'opacity-0 pointer-events-none') : 'opacity-100'
        }`}>
          {currentMedia.description && (
            <p className="text-white text-sm mb-2 leading-relaxed">{currentMedia.description}</p>
          )}
          {/* Tags */}
          {currentMedia.tags && (
            <div className="flex flex-wrap gap-2">
              {(Array.isArray(currentMedia.tags) 
                ? currentMedia.tags 
                : currentMedia.tags.split(',').map(t => t.trim())
              ).map((tag, index) => (
                <span
                  key={index}
                  className="bg-white bg-opacity-20 text-white text-xs px-2 py-1 rounded-full"
                >
                  #{tag}
                </span>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ===== FLOATING ACTION BUTTON & MENU ===== */}
      <div className="fixed bottom-6 right-6 z-30 flex flex-col items-end gap-3">
        {/* Action Menu Items - Stack from bottom to top */}
        {showActionMenu && (
          <div className="flex flex-col gap-3 animate-in slide-in-from-bottom-4 duration-300">
            {/* Like Button */}
            <button
              onClick={handleLike}
              className="flex items-center gap-3 group"
            >
              <div className="bg-white px-4 py-2 rounded-full shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                <span className="text-sm font-medium text-gray-700 whitespace-nowrap">
                  {hasLiked ? 'Unlike' : 'Like'}
                </span>
              </div>
              <div className={`w-12 h-12 rounded-full shadow-lg flex items-center justify-center transition-all duration-300 hover:scale-110 ${
                hasLiked ? 'bg-red-500' : 'bg-pink-500'
              }`}>
                <Heart className={`h-5 w-5 text-white ${hasLiked ? 'fill-white' : ''}`} />
              </div>
            </button>

            {/* Share Button */}
            <button
              onClick={handleShare}
              className="flex items-center gap-3 group"
            >
              <div className="bg-white px-4 py-2 rounded-full shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                <span className="text-sm font-medium text-gray-700 whitespace-nowrap">Share</span>
              </div>
              <div className="w-12 h-12 rounded-full bg-blue-500 shadow-lg flex items-center justify-center transition-all duration-300 hover:scale-110">
                <Share2 className="h-5 w-5 text-white" />
              </div>
            </button>

            {/* Expressions Button */}
            <button
              onClick={handleOpenExpressions}
              className="flex items-center gap-3 group"
            >
              <div className="bg-white px-4 py-2 rounded-full shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                <span className="text-sm font-medium text-gray-700 whitespace-nowrap">Expressions</span>
              </div>
              <div className="w-12 h-12 rounded-full bg-purple-500 shadow-lg flex items-center justify-center transition-all duration-300 hover:scale-110">
                <MessageCircle className="h-5 w-5 text-white" />
              </div>
            </button>

            {/* Voice Note Button */}
            <button
              onClick={handleOpenVoiceRecorder}
              className="flex items-center gap-3 group"
            >
              <div className="bg-white px-4 py-2 rounded-full shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                <span className="text-sm font-medium text-gray-700 whitespace-nowrap">Voice Note</span>
              </div>
              <div className="w-12 h-12 rounded-full bg-orange-500 shadow-lg flex items-center justify-center transition-all duration-300 hover:scale-110">
                <Mic className="h-5 w-5 text-white" />
              </div>
            </button>
          </div>
        )}

        {/* Main Floating Action Button */}
        <button
          onClick={handleToggleActionMenu}
          className={`w-14 h-14 rounded-full shadow-3xl flex items-center justify-center transition-all duration-300 hover:scale-110 ${
            showActionMenu 
              ? 'bg-red-500 text-white rotate-45' 
              : 'bg-gradient-to-br from-indigo-100 via-purple-100 to-white text-gray-400'
          }`}
          title={showActionMenu ? "Close Actions" : "Open Actions"}
        >
          {showActionMenu ? (
            <X className="h-6 w-6" />
          ) : (
            <Plus className="h-6 w-6" />
          )}
        </button>
      </div>

      {/* Swipe instruction for mobile - only when controls are hidden */}
      {isTouchDevice && !controlsVisible && media.length > 1 && (
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-white text-xs opacity-60 pointer-events-none">
          Swipe to navigate • {currentIndex + 1} of {media.length}
        </div>
      )}
    </div>
  );
};